import { useEffect, useState } from 'react';
import { Car } from '@phosphor-icons/react';
import { generateRemoteImgproxyUrl, peekRemoteImgproxyUrl } from '../../../services/imgproxy';
import './AndysPopularCars.css';

// Размер превью = самая широкая плитка 156×117 (CSS, 4:3) × 2 под retina.
// Оригиналы на сайте Andys — 4032×3024, ~0.9MB; превью через imgproxy — ~15KB
// (#1469).
export const POPULAR_THUMB_WIDTH = 312;
export const POPULAR_THUMB_HEIGHT = 234;

/**
 * Фото плитки: сначала просим у b2b-api уменьшенную копию (imgproxy тянет
 * оригинал с сайта дилера), пока ждём — серая подложка. Не вышло (прокси
 * недоступен, origin не в allowlist, картинка не загрузилась) — показываем
 * оригинал: тяжело, но не пусто (#1469).
 */
// Уже известный ответ кеша (ремаунт ленты при смене таба) — сразу, без
// серой подложки: превью, или оригинал, если превью для этой картинки нет.
const cachedPhoto = (src) => {
  const known = peekRemoteImgproxyUrl(src, POPULAR_THUMB_WIDTH, POPULAR_THUMB_HEIGHT);
  return known === undefined ? null : known || src;
};

const PopularCarPhoto = ({ src }) => {
  const [shown, setShown] = useState(() => cachedPhoto(src));

  useEffect(() => {
    const known = cachedPhoto(src);
    if (known) {
      setShown(known);
      return undefined;
    }
    let alive = true;
    setShown(null);
    generateRemoteImgproxyUrl(src, POPULAR_THUMB_WIDTH, POPULAR_THUMB_HEIGHT).then((thumb) => {
      if (alive) setShown(thumb || src);
    });
    return () => {
      alive = false;
    };
  }, [src]);

  if (!shown) {
    return <span className="andys-popular__photo" aria-hidden="true" />;
  }
  return (
    <img
      className="andys-popular__photo"
      src={shown}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => {
        if (shown !== src) setShown(src);
      }}
    />
  );
};

/**
 * AndysPopularCars — «Popular now» на Buy-табе лендинга Andys (#1469):
 * лёгкая горизонтальная лента со scroll-snap, карточка = ссылка на детальную
 * машины на сайте дилера.
 *
 * Не `SalonCarCard` + `AutoScrollCarousel`: та карточка — тёмная салонная
 * (Autodis), с фото от 220px и брендовым бейджем, и тянет контексты
 * DutyFree/I18n; карусель сама едет по экрану. Здесь нужна компактная светлая
 * плитка на ~360px экране, которую листают пальцем.
 *
 * Презентационный: слайды уже посчитаны контейнером (`buildPopularCarSlides`).
 * Пустой список (инвентарь ещё грузится или машин нет) — секции нет вовсе.
 *
 * @param {object} props
 * @param {Array<{id: string, title: string, year?: number|null, price: string, priceValue?: number|null, image?: string|null, href: string}>} props.cars
 * @param {string} [props.title]
 * @param {(car: object, index: number) => void} [props.onCarClick] — тап по
 *   плитке, до перехода по ссылке (аналитика, #1482).
 */
export const AndysPopularCars = ({ cars, title = 'Popular now', onCarClick }) => {
  if (!Array.isArray(cars) || cars.length === 0) return null;

  const titleId = 'andys-popular-title';

  return (
    <section className="andys-popular" aria-labelledby={titleId}>
      <h2 id={titleId} className="andys-popular__title">
        {title}
      </h2>
      <ul className="andys-popular__row">
        {cars.map((car, index) => (
          <li key={car.id} className="andys-popular__item">
            <a className="andys-popular__card" href={car.href} onClick={() => onCarClick?.(car, index)}>
              {car.image ? (
                <PopularCarPhoto src={car.image} />
              ) : (
                <span className="andys-popular__photo andys-popular__photo--empty" aria-hidden="true">
                  <Car size={32} weight="regular" />
                </span>
              )}
              {/* Пробелы между частями — для имени ссылки у скринридера
                  («Toyota Yaris 2019 €12,500»), визуально их съедает flex. */}
              <span className="andys-popular__body">
                <span className="andys-popular__name">{car.title}</span>{' '}
                <span className="andys-popular__meta">
                  {car.year ? <span className="andys-popular__year">{car.year}</span> : null}{' '}
                  <span className="andys-popular__price">{car.price}</span>
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default AndysPopularCars;
